/**
 * The agent's computer-view verbs — COMPUTER_SHOW / COMPUTER_CLOSE (founder
 * 2026-08-31: "can agent open and close computer by itself… open computer and
 * go to this app / open this preview in computer").
 *
 * Live socket events only — they are relay-forwarded from the sandbox tool
 * fire and never replayed, so an old task's history can never pop the
 * computer open (the #691 replay trap the design dodges by construction).
 *
 * A SHOW is the user's own ask carried through the agent, so it overrides a
 * sticky manual close (showSurface / openWithApp / openPreviewTarget all
 * reset hasUserClosed) — unlike the ambient auto-open paths, which respect it.
 */
import type { Socket } from 'socket.io-client';
import { EVENTS, type ComputerShowPayload, type ComputerClosePayload } from '@bash/shared';
import { useComputerStore } from '../computerStore';
import { targetFromArtifact } from '@/components/files-browser/preview-target';

const SURFACES = new Set(['browser', 'files', 'app', 'terminal', 'previews']);

export function handleComputerShow(payload: ComputerShowPayload): void {
  if (!payload?.taskId || !SURFACES.has(payload.surface)) return;
  const cs = useComputerStore.getState();
  // A named creation opens itself, full screen — the identity rides along
  // from present_output's own result, so no lookup is needed here.
  if (payload.surface === 'previews' && payload.url) {
    cs.openPreviewTarget(
      payload.taskId,
      targetFromArtifact({
        id: payload.id ?? '',
        url: payload.url,
        kind: 'file',
        filename: null,
        caption: payload.name ?? null,
      }),
    );
    return;
  }
  if (payload.surface === 'app') { cs.openWithApp(payload.taskId); return; }
  if (payload.surface === 'previews') {
    cs.showSurface(payload.taskId, 'files');
    cs.openPreviews(payload.taskId);
    return;
  }
  cs.showSurface(payload.taskId, payload.surface);
}

export function handleComputerClose(payload: ComputerClosePayload): void {
  if (!payload?.taskId) return;
  const cs = useComputerStore.getState();
  cs.closeWindow(payload.taskId);
  cs.close(payload.taskId);
}

export function attachComputerVerbHandler(socket: Socket): () => void {
  socket.on(EVENTS.COMPUTER_SHOW, handleComputerShow);
  socket.on(EVENTS.COMPUTER_CLOSE, handleComputerClose);
  return () => {
    socket.off(EVENTS.COMPUTER_SHOW, handleComputerShow);
    socket.off(EVENTS.COMPUTER_CLOSE, handleComputerClose);
  };
}
